import React, { useState } from "react";
import { Link } from "react-router-dom";
import { BLOG_POSTS } from "@/data/content";
import JsonLd from "@/components/JsonLd";
import useSEO from "@/lib/useSEO";
import { blogSchema, breadcrumbSchema, SITE_URL } from "@/lib/schema";

export default function Blog() {
  useSEO({
    title:
      "Insights — Stenography Speed Building & AI Legal Drafting | LexWorkspace",
    description:
      "Practical writing for stenographers, court reporters, attorneys and legal teams. Speed building, RPR/RMR/CRR prep, AI legal drafting, automated contract creation, and city-specific guides for DC, NYC, LA, Chicago and Houston.",
    keywords:
      "stenography blog, court reporting blog, AI legal drafting blog, automated contract drafting tool, legal AI insights",
    url: SITE_URL + "/blog",
  });

  const [filter, setFilter] = useState("all");
  const posts = BLOG_POSTS.filter((p) =>
    filter === "all" ? true : p.product === filter
  );

  return (
    <div data-testid="blog-page">
      <JsonLd id="blog-list" data={blogSchema(BLOG_POSTS)} />
      <JsonLd
        id="blog-breadcrumbs"
        data={breadcrumbSchema([
          { name: "Home", url: SITE_URL + "/" },
          { name: "Insights", url: SITE_URL + "/blog" },
        ])}
      />
      <section className="border-b border-black/10">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-24 lg:py-32">
          <p className="text-xs tracking-[0.3em] uppercase text-gray-500 mb-6">
            Insights
          </p>
          <h1 className="font-serif-display text-5xl sm:text-6xl lg:text-7xl leading-none max-w-4xl">
            Practical writing for stenographers and lawyers.
          </h1>
          <p className="text-gray-600 mt-6 max-w-2xl leading-relaxed">
            Tight, useful articles on stenography speed building, certification
            prep, AI legal drafting, contract automation and the realities of
            running a modern legal practice.
          </p>

          <div className="mt-12 flex flex-wrap gap-3">
            {[
              { key: "all", label: "All" },
              { key: "steno", label: "Stenography" },
              { key: "draftmaster", label: "Legal AI" },
            ].map((t) => (
              <button
                key={t.key}
                data-testid={`blog-filter-${t.key}`}
                onClick={() => setFilter(t.key)}
                className={
                  "text-xs tracking-[0.2em] uppercase font-semibold px-5 py-2.5 border transition-colors " +
                  (filter === t.key
                    ? "bg-black text-white border-black"
                    : "border-black/20 text-[#111] hover:bg-black hover:text-white")
                }
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {posts.map((p) => (
            <Link
              key={p.slug}
              to={`/blog/${p.slug}`}
              data-testid={`blog-card-${p.slug}`}
              className="group border border-black/10 bg-white hover:shadow-lg transition-shadow"
            >
              <div className="aspect-[16/10] overflow-hidden">
                <img
                  src={p.image}
                  alt={p.title}
                  loading="lazy"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
              </div>
              <div className="p-6">
                <p
                  className={
                    "text-xs tracking-[0.2em] uppercase font-semibold mb-3 " +
                    (p.product === "steno"
                      ? "text-[#1E3A8A]"
                      : "text-[#065F46]")
                  }
                >
                  {p.product === "steno" ? "Stenography" : "Legal AI"}
                </p>
                <h3 className="font-serif-display text-2xl mb-3 leading-tight">
                  {p.title}
                </h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  {p.excerpt}
                </p>
              </div>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}
